/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { useCallback, useEffect, useMemo } from "react";

import {
  checkIfContainsPath,
  useConfigurationSelector,
  UserSubscriptionStatus,
  useSubscriptionsSelector,
  useUserSubprofilesSelector,
} from "@nf/common";
import {
  useRedirectToConsentPage,
  useRouter,
  useCountry,
  useLanguage,
} from "../../hooks";

import { ROUTES, paymentPaths } from "@nf/constants";
interface IRoutProtectorProps {
  children: JSX.Element;
  isLoggedIn?: boolean;
}

export const RouteProtector = ({
  children,
  isLoggedIn,
}: IRoutProtectorProps) => {
  const router = useRouter();
  const configuration = useConfigurationSelector();
  const { subscriptions } = useSubscriptionsSelector();
  const { Data: subprofilesState } = useUserSubprofilesSelector();

  const country = useCountry();
  const language = useLanguage();

  const currentSubprofile = subprofilesState?.currentSubprofile;
  const mainChannels = [
    configuration?.Routes?.HOME,
    configuration?.Routes?.DANSK,
  ].filter((path) => path !== undefined) as string[];

  const shouldRedirectToKidsChannel = useMemo(
    () =>
      currentSubprofile?.Child &&
      checkIfContainsPath(router.asPath, mainChannels),
    [router.asPath, currentSubprofile?.Child]
  );

  const onPopState = useCallback(() => {
    if (
      currentSubprofile?.Child &&
      checkIfContainsPath(location.href, mainChannels) &&
      configuration?.Routes?.KIDS
    ) {
      router.replace(configuration.Routes.KIDS || "");

      history.pushState(
        null,
        document.title,
        `${location.origin}${router.asPath}`
      );
    }
  }, [currentSubprofile?.Child, router.asPath]);

  useEffect(() => {
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  useRedirectToConsentPage(isLoggedIn);

  useEffect(() => {
    const isPaymentPath = checkIfContainsPath(router.asPath, paymentPaths);
    const hasActiveSubscription =
      Array.isArray(subscriptions) &&
      subscriptions?.some(
        (subscription) =>
          subscription.Status === UserSubscriptionStatus.ACTIVE ||
          subscription.Status === UserSubscriptionStatus.CANCELLED // still active until the end of subscription period
      );
    const hasPausedSubscription =
      Array.isArray(subscriptions) &&
      subscriptions?.some(
        (subscription) => subscription.Status === UserSubscriptionStatus.HOLD
      );

    if (isPaymentPath && (hasActiveSubscription || hasPausedSubscription)) {
      router.replace(ROUTES.SETTINGS_SUBSCRIPTION);
    }
  }, [router.asPath, subscriptions]);

  useEffect(() => {
    if (
      isLoggedIn &&
      (router.asPath === ROUTES.BASE ||
        router.asPath.startsWith(`${ROUTES.BASE}?`) || // When redirected from Google or from anywhere
        router.asPath === `/${country}/${language}/`)
    ) {
      router.replace(
        currentSubprofile?.InitialHomePage ??
          (configuration?.Routes?.HOME || "/"),
        undefined,
        { withLoadingSplash: true }
      );
    }
  }, [
    isLoggedIn,
    router.asPath,
    country,
    language,
    currentSubprofile?.InitialHomePage,
    configuration,
  ]);

  useEffect(() => {
    if (shouldRedirectToKidsChannel && configuration?.Routes?.KIDS) {
      router.replace(configuration?.Routes?.KIDS, undefined, {
        withLoadingSplash: true,
      });
    }
  }, [shouldRedirectToKidsChannel]);

  return children;
};

export default RouteProtector;
